import React, { useMemo, useState } from "react";
import { Button, CircularProgress } from "@mui/material";
import { useNavigate } from "react-router-dom";
import AppTableDateTime from "components/Shared/AppTableDateTime";
import PrintOutlined from "@mui/icons-material/PrintOutlined";
import ConfirmationNumberOutlined from "@mui/icons-material/ConfirmationNumberOutlined";
import AppTable from "components/Shared/AppTable";
import BreadcrumbLayout from "layouts/BreadcrumbLayout";
import { useAppMeta, useAuth } from "hooks";
import HighlightedText from "components/Shared/HighlightedText";
import { useLazyListVendorPendingPaymentsPrintForVendorQuery } from "store/services/reportApi";
import { handleVendorPendingPaymentPrint } from "../../Reports/PaymentReports/VendorReports/HandleVendorPendingPaymentPrint";

const PAGE_SIZE_OPTIONS = [10, 25, 50];

const VendorPendingCodListScreen = () => {
    const loggedInUser = useAuth();
    const navigate = useNavigate();
    const [keyword, setKeyword] = useState("");
    const [currentPage, setCurrentPage] = useState(1);
    const [perPage, setPerPage] = useState(PAGE_SIZE_OPTIONS[0]);

    const [fetchVendorPendingPayments, { data: response, isLoading, isFetching }] =
        useLazyListVendorPendingPaymentsPrintForVendorQuery();

    const breadcrumbs = [
        { title: "COD Transfer", path: "/app/vendor/cod-transfer/list" },
        { title: "Pending COD" },
    ];

    const vendorId = loggedInUser?.loggedInUser?.typeRef;

    // Fetch once on mount (all data — server doesn't support pagination/search)
    React.useEffect(() => {
        if (!vendorId) return;
        fetchVendorPendingPayments({ vendorId });
    }, [fetchVendorPendingPayments, vendorId]);

    // Client-side filter + pagination
    // eslint-disable-next-line react-hooks/exhaustive-deps
    const allRows = response?.data ?? [];

    const filteredRows = useMemo(() => {
        if (!keyword.trim()) return allRows;
        const lower = keyword.toLowerCase();
        return allRows.filter((row) =>
            row?.shipmentId?.toLowerCase().includes(lower)
        );
    }, [allRows, keyword]);

    const totalItems = filteredRows.length;
    const paginatedRows = useMemo(() => {
        const start = (currentPage - 1) * perPage;
        return filteredRows.slice(start, start + perPage);
    }, [filteredRows, currentPage, perPage]);

    // Reset to page 1 whenever the search keyword changes
    React.useEffect(() => {
        setCurrentPage(1);
    }, [keyword]);

    const handlePageChange = (page) => setCurrentPage(page);
    const handlePerRowsChange = (newPerPage) => {
        setPerPage(newPerPage);
        setCurrentPage(1);
    };

    const handlePrintClick = async () => {
        if (!vendorId) return;
        await handleVendorPendingPaymentPrint(vendorId, fetchVendorPendingPayments);
    };

    const columns = [
        {
            name: "SN",
            cell: (_row, index) => (currentPage - 1) * perPage + index + 1,
            width: "60px",
        },
        {
            name: "Shipment ID",
            selector: (row) => row?.shipmentId,
            cell: (row) =>
                row?.shipmentId ? (
                    <HighlightedText text={row.shipmentId} highlight={keyword} />
                ) : (
                    "N/A"
                ),
            minWidth: "200px",
        },
        {
            name: "Shipment Status",
            selector: (row) => row?.shipmentStatus,
            cell: (row) => row?.shipmentStatus || "N/A",
            minWidth: "150px",
        },
        {
            name: "COD",
            selector: (row) => row?.codAmount,
            cell: (row) =>
                row?.codAmount != null ? row.codAmount.toFixed(2) : "N/A",
            minWidth: "120px",
            right: true,
        },
        {
            name: "Charge",
            selector: (row) => row?.chargeAmount,
            cell: (row) =>
                row?.chargeAmount != null ? row.chargeAmount.toFixed(2) : "N/A",
            minWidth: "120px",
            right: true,
        },
        {
            name: "Created Date",
            selector: (row) => row?.createdAt,
            cell: (row) =>
                row?.createdAt ? (
                    <AppTableDateTime date={row.createdAt} />
                ) : (
                    "N/A"
                ),
            minWidth: "160px",
        },
    ];

    useAppMeta("Pending COD");

    return (
        <BreadcrumbLayout
            breadcrumbs={breadcrumbs}
            headerActions={
                <div className="flex items-center gap-2">
                <Button
                    variant="contained"
                    startIcon={<ConfirmationNumberOutlined />}
                    onClick={() => navigate("/app/vendor/ticket/create")}
                    color="primary"
                    size="medium"
                >
                    Create COD Ticket
                </Button>
                <Button
                    variant="contained"
                    startIcon={
                        isLoading || isFetching ? (
                            <CircularProgress size={18} color="inherit" />
                        ) : (
                            <PrintOutlined />
                        )
                    }
                    onClick={handlePrintClick}
                    disabled={isLoading || isFetching || !allRows.length}
                    color="primary"
                    size="medium"
                >
                    Print All
                </Button>
                </div>
            }
        >
            <BreadcrumbLayout.Paper>

                <AppTable
                    columns={columns}
                    data={paginatedRows}
                    paginationServer={false}     
                    pagination
                    paginationPerPage={perPage}
                    paginationDefaultPage={currentPage}
                    paginationRowsPerPageOptions={PAGE_SIZE_OPTIONS}
                    onChangePage={handlePageChange}
                    onChangeRowsPerPage={handlePerRowsChange}
                    progressPending={isLoading || isFetching}
                    paginationTotalRows={totalItems}
                    noDataComponent={
                        keyword
                            ? `No shipments match "${keyword}"`
                            : "No pending COD payments found."
                    }
                />
            </BreadcrumbLayout.Paper>
        </BreadcrumbLayout>
    );
};

export default VendorPendingCodListScreen;